import { Box, Stack, Typography } from "@mui/material";
import type { GridApiPremium, GridColDef, GridDataSource } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import {
  getRowSelectionModelFromIds,
  getSelectedRowIdsFromModel,
  GRID_DEFAULT_PAGE_SIZE,
} from "@entrilia/shared/components/grid/gridHelper";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import PreviewFileDialog from "@entrilia/shared/components/previewFile/PreviewFileDialog";
import CompletionPercentageIndicator from "../../../common/CompletionPercentageIndicator";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import { getRowsByIds } from "@entrilia/shared/utilities/dataGridApiHelper";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import { formatDurationInSeconds } from "@entrilia/shared/utilities/formatters";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { Category } from "../../../../api/types/accessTypes";
import type {
  DocumentCollectionOperations,
  InvestorPortalMessageSource,
} from "../../../../api/types/documentCollectionTypes";
import type { FundStructureDocument } from "../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import { investorPortalMessageSourcesCaptionsMap } from "../../../../utilities/enumCaptions";
import CategoryTag from "../../../common/CategoryTag";
import { getCheckedGridSx } from "../../../common/grid/gridStyles";
import LockedGridCell from "../../../common/LockedGridCell";
import GridTextHoverAction from "@entrilia/shared/components/grid/GridTextHoverAction";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import ChangeDocumentCollectionsStatusDialog from "../../insights/ChangeDocumentCollectionsStatusDialog";
import DocumentCollectionStatusTag from "../../insights/DocumentCollectionStatusTag";
import DocumentPortalStatusTag from "../../insights/DocumentPortalStatusTag";
import FundStructureDocumentsGridActions from "./FundStructureDocumentsGridActions";
import FundStructureDocumentsGridToolbar from "./FundStructureDocumentsGridToolbar";
import { LockedDocumentActivityPopover } from "./LockedPortalPagePopovers";

const getViewsInfo = (row: FundStructureDocument) => {
  const entry = Object.entries(row.attachmentsWithViews ?? {})?.find(
    ([, value]) => value.totalViews || value.totalDownloads
  );

  return entry ? { attachmentId: entry[0], viewsInfo: entry[1] } : undefined;
};

const getColumnDefinitions = (
  onDocumentPreview: (doc: FundStructureDocument) => void,
  onDocumentDownload: (doc: FundStructureDocument) => void,
  getDocCollectionPath: (doc: FundStructureDocument) => string | undefined,
  categories: Category[],
  excludeColumns: string[],
  isInvestorPortalAppEnabled: boolean,
  onNavigateToDocumentActivity?: (postMessageRequestId: string, attachmentId: string) => void
): GridColDef<FundStructureDocument>[] => {
  const columnDefs: GridColDef<FundStructureDocument>[] = [
    {
      field: nameof<FundStructureDocument>("name"),
      headerName: "Name",
      flex: 1,
      minWidth: 160,
      cellClassName: "highlighted-action",
      renderCell: ({ row }) => (
        <GridTextHoverAction onClick={() => onDocumentPreview(row)}>{row.name}</GridTextHoverAction>
      ),
    },
    {
      field: nameof<FundStructureDocument>("investorNames"),
      headerName: "Investor",
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) =>
        row.fundNames.length === 0 ? (
          "-"
        ) : (
          <InlineItemsList<string> displayCount={1} items={distinct(row.investorNames)} propGetter={(prop) => prop} />
        ),
    },
    {
      field: nameof<FundStructureDocument>("fundNames"),
      headerName: "Fund",
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) =>
        row.fundNames.length === 0 ? (
          "-"
        ) : (
          <InlineItemsList<string> displayCount={1} items={distinct(row.fundNames)} propGetter={(prop) => prop} />
        ),
    },
    {
      field: nameof<FundStructureDocument>("categoryId"),
      headerName: "Category",
      flex: 1,
      minWidth: 250,
      renderCell: ({ row }) => <CategoryTag externalCategoryId={row.categoryId} />,
    },
    {
      field: nameof<FundStructureDocument>("documentCollectionNo"),
      headerName: "Collection ID",
      type: "number",
      flex: 0.5,
      minWidth: 100,
      cellClassName: "highlighted-action",
      renderCell: ({ row }) => (
        <GridTextHoverLink to={getDocCollectionPath(row)}>
          {row.documentCollectionNo?.toLocaleString()}
        </GridTextHoverLink>
      ),
    },
    {
      field: nameof<FundStructureDocument>("documentCollectionName"),
      headerName: "Collection Name",
      flex: 1,
      minWidth: 160,
      cellClassName: "highlighted-action",
      renderCell: ({ row }) => (
        <GridTextHoverLink to={getDocCollectionPath(row)}>{row.documentCollectionName}</GridTextHoverLink>
      ),
    },
    ...(isInvestorPortalAppEnabled
      ? ([
          {
            field: nameof<FundStructureDocument>("publishToInvestorPortal"),
            headerName: "Investor Portal",
            width: 160,
            renderCell: ({ row }) => <DocumentPortalStatusTag isPublished={row.publishToInvestorPortal} />,
          },
        ] satisfies GridColDef<FundStructureDocument>[])
      : []),
    {
      field: nameof<FundStructureDocument>("status"),
      headerName: "Status",
      width: 128,
      renderCell: ({ row }) => <DocumentCollectionStatusTag status={row.status} />,
    },
    {
      field: nameof<FundStructureDocument>("messageSource"),
      headerName: "Source",
      flex: 1,
      minWidth: 120,
      valueFormatter: (value: InvestorPortalMessageSource) => investorPortalMessageSourcesCaptionsMap[value],
    },
    {
      field: nameof<FundStructureDocument>("sentAt"),
      headerName: "Publish Date",
      width: 120,
      valueFormatter: (value: string) => convertISODate(value),
    },
  ];

  if (isInvestorPortalAppEnabled) {
    columnDefs.push(
      {
        field: "views",
        headerName: "Views",
        type: "number",
        width: 100,
        valueGetter: (_, row) => getViewsInfo(row)?.viewsInfo.totalViews || null,
      },
      {
        field: "downloads",
        headerName: "Downloads",
        type: "number",
        width: 100,
        valueGetter: (_, row) => getViewsInfo(row)?.viewsInfo.totalDownloads || null,
      },
      {
        field: "totalViewTimeSeconds",
        headerName: "Total Time Spent",
        headerAlign: "right",
        align: "right",
        width: 140,
        valueGetter: (_, row) => {
          const totalViewTimeSeconds = getViewsInfo(row)?.viewsInfo.totalViewTimeSeconds;
          return totalViewTimeSeconds ? formatDurationInSeconds(totalViewTimeSeconds) : null;
        },
      },
      {
        field: "totalCompletionPercentage",
        headerName: "Total Completion",
        type: "number",
        width: 160,
        headerAlign: "center",
        align: "center",
        renderCell: ({ row }) => {
          const completionPercentage = getViewsInfo(row)?.viewsInfo.totalCompletionPercentage;
          if (!completionPercentage) {
            return null;
          }

          return <CompletionPercentageIndicator value={completionPercentage} />;
        },
      }
    );
  } else {
    columnDefs.push({
      field: "views",
      resizable: false,
      width: 160,
      renderHeader: () => (
        <Stack spacing={0.5} direction="row" sx={{ width: "100%", alignItems: "center" }}>
          <Typography color="textDisabled">Portal Analytics</Typography>
          <LockedDocumentActivityPopover />
        </Stack>
      ),
      renderCell: () => <LockedGridCell />,
    });
  }

  columnDefs.push({
    field: "actions",
    headerName: "Actions",
    width: 90,
    cellClassName: "grid-row-actions",
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }) => (
      <FundStructureDocumentsGridActions
        row={row}
        attachmentId={getViewsInfo(row)?.attachmentId}
        onNavigateToDocumentActivity={onNavigateToDocumentActivity}
        onDownloadClick={onDocumentDownload}
      />
    ),
  });

  return columnDefs.filter((c) => !excludeColumns.includes(c.field));
};

interface Props {
  dataSource: GridDataSource;
  categories: Category[];
  excludeColumns?: string[];
  onNavigateToDocumentActivity?: (postMessageRequestId: string, attachmentId: string) => void;
  allowStatusChanges?: boolean;
  onRefreshDocuments?: () => void;
  apiRef: RefObject<GridApiPremium | null>;
  lazyLoading?: boolean;
}

interface DialogState {
  openDialog?: "change_status";
  operation?: DocumentCollectionOperations;
  postMessageRequestIds?: string[];
  documentsInfo?: string;
}

const getDocCollectionFileDownloadInfo = withErrorHandling(adminApi.getDocCollectionFileDownloadInfo);
const getDownloadUrlForSelectedDocuments = withErrorHandling(adminApi.getDownloadUrlForSelectedDocuments);

const FundStructureDocumentsGrid = ({
  dataSource,
  categories,
  excludeColumns,
  onNavigateToDocumentActivity,
  allowStatusChanges,
  onRefreshDocuments,
  apiRef,
  lazyLoading,
}: Props) => {
  const { clientCode, investorPortalApp } = useClientContext();
  const { sendNotificationError } = useNotificationContext();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dialogState, setDialogState] = useState<DialogState>({});
  const [documentPreviewState, setDocumentPreviewState] = useState<FileDownloadInfo>();
  const [dataSourceError, setDataSourceError] = useState<Error>();

  const rowSelectionModel = useMemo(() => getRowSelectionModelFromIds(selectedIds), [selectedIds]);

  const handleDataSourceError = useCallback((error: Error) => {
    logError(error, "[FundStructureDocumentsGrid]");
    setDataSourceError(error);
  }, []);

  const getFileDownloadInfo = useCallback(
    async (document: FundStructureDocument) => {
      const [attachmentId] = document.attachmentIds;
      if (!attachmentId || !document.documentCollectionId) {
        return undefined;
      }

      const [downloadInfo, error] = await getDocCollectionFileDownloadInfo(document.postMessageRequestId, attachmentId);
      if (error) {
        logError(error, "[FundStructureDocumentsGrid] downloadFileData");
        sendNotificationError(error.message);
        return undefined;
      }

      return downloadInfo;
    },
    [sendNotificationError]
  );

  const handleDownloadDocument = useCallback(
    async (document: FundStructureDocument) => {
      const fileInfo = await getFileDownloadInfo(document);
      if (fileInfo) {
        await downloadFileFromUrl(fileInfo.downloadUrl);
      }
    },
    [getFileDownloadInfo]
  );

  const handlePreviewDocument = useCallback(
    async (document: FundStructureDocument) => {
      const fileInfo = await getFileDownloadInfo(document);
      if (fileInfo) {
        setDocumentPreviewState(fileInfo);
      }
    },
    [getFileDownloadInfo]
  );

  const getDocumentCollectionPath = useCallback(
    (document: FundStructureDocument) =>
      document.documentCollectionId
        ? `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.documentCollections}/${document.documentCollectionId}`
        : undefined,
    [clientCode]
  );

  const handleDownloadSelectedDocuments = async () => {
    const selectedDocuments = getRowsByIds<FundStructureDocument>(apiRef, selectedIds);
    const documentIdentifiers = selectedDocuments.flatMap((doc) =>
      doc.attachmentIds.map((id) => ({
        id,
        postMessageRequestId: doc.postMessageRequestId,
      }))
    );

    const [url, error] = await getDownloadUrlForSelectedDocuments(documentIdentifiers, clientCode);
    if (error) {
      logError(error, "[FundStructureDocumentsGrid] getDownloadUrlForSelectedDocuments");
      sendNotificationError(error.message);
      return;
    }

    await downloadFileFromUrl(url);
  };

  const handleChangeStatusOfSelectedDocuments = (operation: DocumentCollectionOperations) => {
    const selectedDocuments = getRowsByIds<FundStructureDocument>(apiRef, selectedIds);
    const postMessageRequestIds = selectedDocuments.map((doc) => doc.postMessageRequestId);
    const documentsInfo = selectedIds.length === 1 ? selectedDocuments[0]?.name : `${selectedIds.length} documents`;

    setDialogState({
      openDialog: "change_status",
      operation,
      postMessageRequestIds,
      documentsInfo,
    });
  };

  const handleChangeStatusApplied = () => {
    setTimeout(() => {
      onRefreshDocuments?.();
    }, 1_000);
  };

  const columns = useMemo(
    () =>
      getColumnDefinitions(
        handlePreviewDocument,
        handleDownloadDocument,
        getDocumentCollectionPath,
        categories,
        excludeColumns ?? [],
        Boolean(investorPortalApp),
        onNavigateToDocumentActivity
      ),
    [
      handlePreviewDocument,
      handleDownloadDocument,
      getDocumentCollectionPath,
      categories,
      excludeColumns,
      investorPortalApp,
      onNavigateToDocumentActivity,
    ]
  );

  const showToolbar = selectedIds.length > 0;

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading documents failed" />;
  }

  return (
    <>
      <Box sx={{ height: "100%" }}>
        <DataGridHeaderToolbarProvider
          showToolbar={showToolbar}
          selection={[selectedIds, setSelectedIds]}
          actions={
            <FundStructureDocumentsGridToolbar
              selectedIds={selectedIds}
              apiRef={apiRef}
              allowStatusChanges={!!allowStatusChanges}
              onDownload={handleDownloadSelectedDocuments}
              onChangeStatus={handleChangeStatusOfSelectedDocuments}
            />
          }
        >
          <DataGrid<FundStructureDocument>
            apiRef={apiRef}
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newSelection) => setSelectedIds(getSelectedRowIdsFromModel(newSelection))}
            sx={getCheckedGridSx}
            columns={columns}
            lazyLoading={lazyLoading}
            dataSource={dataSource}
            paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
            onDataSourceError={handleDataSourceError}
            noRowsText="No documents"
            slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
            disableColumnReorder
            disableColumnSorting
          />
        </DataGridHeaderToolbarProvider>
      </Box>

      {documentPreviewState && (
        <PreviewFileDialog
          url={documentPreviewState.downloadUrl}
          fileName={documentPreviewState.fileName}
          onClose={() => setDocumentPreviewState(undefined)}
        />
      )}

      {dialogState.openDialog === "change_status" &&
        dialogState.postMessageRequestIds &&
        dialogState.operation &&
        dialogState.documentsInfo && (
          <ChangeDocumentCollectionsStatusDialog
            operationLevel="documents"
            selectedIds={dialogState.postMessageRequestIds}
            operation={dialogState.operation}
            documentsInfo={dialogState.documentsInfo}
            onApply={handleChangeStatusApplied}
            onClose={() => setDialogState({})}
          />
        )}
    </>
  );
};

export default FundStructureDocumentsGrid;
